import { useMemo, useState, useEffect } from 'react'
import { Modal, Form, Input, Button, Alert, Divider } from 'antd'
import { AiAgentLogo } from './AiAgentLogo'
import { buildSimpleEmailMessage } from '../libs/email.utils'
import { extractPageNumber } from '../libs/policy-reference.utils'
import { AiSimpleEditor } from './AiSimpleEditor'
import { useAppSelector } from 'store'
import { sendAiResponseEmail } from '../ai-agent.service'

// Simple, reusable disclaimer constants
const AI_DISCLAIMER_TEXT =
  'AI Disclaimer: This report was automatically generated by AL, PolicyComparison’s AI assistant. AL analyses insurer product wordings to identify and summarise key features for comparison purposes. While every effort is made to ensure accuracy, this content may contain errors or omissions and should not be solely relied upon. Please review the source documents and exercise professional judgment.'
const DISCLAIMER_HTML = [
  "<div style='margin-top:16px; border-top:1px solid #f0f0f0; padding-top:10px;'>",
  `<div style='font-size:12px; line-height:1.45; color:#595959;'>${AI_DISCLAIMER_TEXT}</div>`,
  '</div>',
].join('')

function rewritePdfLinks(sourceHtml: string, mediaUrl: string): string {
  if (!sourceHtml) return sourceHtml
  if (!mediaUrl) return sourceHtml

  const container = document.createElement('div')
  container.innerHTML = sourceHtml

  const anchors = container.querySelectorAll('a.policy-page-link')
  anchors.forEach((a) => {
    const pdfPath = a.getAttribute('data-pdf-path') || ''
    if (!pdfPath) return
    const pageRef = a.getAttribute('data-text') || ''
    const pageNumber = extractPageNumber(pageRef || '')
    const href = `${mediaUrl}/${pdfPath}${pageNumber ? `#page=${pageNumber}` : ''}`
    a.setAttribute('href', href)
    a.setAttribute('target', '_blank')
    a.setAttribute('rel', 'noopener noreferrer')
  })

  return container.innerHTML
}

function appendDisclaimer(html: string): string {
  const safe = html || ''
  if (!safe) return DISCLAIMER_HTML
  if (safe.toLowerCase().includes('ai disclaimer')) return safe
  return `${safe}${DISCLAIMER_HTML}`
}

function buildFinalHtml(rawHtml: string, mediaUrl: string): string {
  return appendDisclaimer(rewritePdfLinks(rawHtml || '', mediaUrl))
}

interface AiAgentEmailModalProps {
  open: boolean
  onClose: () => void
  html?: string
  subject?: string
}

function AiAgentEmailModal({ open, onClose, html, subject }: AiAgentEmailModalProps) {
  const [form] = Form.useForm()
  const [sending, setSending] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [emailError, setEmailError] = useState<string | null>(null)
  const account = useAppSelector((state) => state.auth.account)
  const MEDIA_URL: string = process.env.REACT_APP_MEDIA_BASE_URL || ''

  const brokerName = useMemo(() => {
    if (account?.name && typeof account.name === 'string') return account.name
    if (account?.email && typeof account.email === 'string') return account.email.split('@')[0]
    return 'Your Broker'
  }, [account])

  const stockEmailMessageHtml = useMemo(() => {
    return [
      '<p>Hi,</p>',
      '<p>Please find a brief summary of the key differences between the selected insurance policies below.</p>',
      '<p>Let me know if you would like a deeper breakdown, a full comparison table, or clarifications on any features.</p>',
      `<p>Regards,<br/>${brokerName}</p>`,
    ].join('')
  }, [brokerName])

  const initialValues = useMemo(() => {
    return {
      subject: subject || 'Policy Comparison Insight - AL Response',
      cc: account?.email,
      message: html ? rewritePdfLinks(html, MEDIA_URL) : stockEmailMessageHtml,
    }
  }, [subject, account?.email, html, MEDIA_URL, stockEmailMessageHtml])

  useEffect(() => {
    if (open) {
      form.resetFields()
      form.setFieldsValue(initialValues)
      setShowPreview(false)
      setEmailError(null)
    }
  }, [open, initialValues, form])

  async function handleSend(values: { to: string; cc?: string; subject: string; message: string }) {
    setSending(true)
    setEmailError(null)
    try {
      const baseHtml = values.message || html || ''
      const finalHtml = buildFinalHtml(baseHtml, MEDIA_URL)
      const plainText = buildSimpleEmailMessage({ html: finalHtml })

      await sendAiResponseEmail({
        to: values.to,
        cc: values.cc,
        subject: values.subject,
        html: finalHtml,
        text: plainText,
      })

      // Show success feedback before closing
      setTimeout(() => {
        onClose()
        form.resetFields()
        setShowPreview(true)
        setEmailError(null)
      }, 500)
    } catch (error) {
      setEmailError(
        error instanceof Error ? error.message : 'Failed to send email. Please try again.',
      )
    } finally {
      setSending(false)
    }
  }

  // Get current form values for preview
  const currentValues = Form.useWatch([], form)
  const previewHtml = useMemo(() => {
    if (!currentValues?.message) return ''
    return buildFinalHtml(currentValues.message, MEDIA_URL)
  }, [currentValues?.message, MEDIA_URL])

  return (
    <Modal
      open={open}
      onCancel={onClose}
      className='ai-agent-email-modal'
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <AiAgentLogo className='ai-agent-avatar' size={24} />
          <span style={{ marginLeft: 8 }}>Email AL Response</span>
        </div>
      }
      footer={null}
      destroyOnClose
      width={'max(80vw, 800px)'}
      centered
      aria-labelledby='email-modal-title'
      aria-describedby='email-modal-description'
    >
      <div className='ai-agent-email-modal-content'>
        <div id='email-modal-description' className='sr-only'>
          Compose and send an email with the AI agent&apos;s response. Fill in recipient details and
          customize the message before sending.
        </div>

        {emailError && (
          <Alert
            message='Email Error'
            description={emailError}
            type='error'
            showIcon
            closable
            onClose={() => setEmailError(null)}
            style={{ marginBottom: 16 }}
            role='alert'
            aria-live='polite'
          />
        )}

        <Form form={form} layout='vertical' initialValues={initialValues} onFinish={handleSend}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Form.Item
              name='to'
              label="Recipient's Email"
              rules={[{ required: true, type: 'email' }]}
            >
              <Input
                placeholder='client@example.com'
                size='large'
                aria-describedby='to-field-help'
                autoComplete='email'
              />
            </Form.Item>

            <Form.Item
              name='cc'
              label='CC (This address will be added CC of email)'
              rules={[{ type: 'email' }]}
            >
              <Input
                placeholder='me@broker.com'
                size='large'
                aria-describedby='cc-field-help'
                autoComplete='email'
              />
            </Form.Item>
          </div>

          <Form.Item name='subject' label='Subject' rules={[{ required: true }]}>
            <Input
              placeholder='Enter email subject'
              size='large'
              aria-describedby='subject-field-help'
            />
          </Form.Item>

          <Form.Item name='message' label='Message' rules={[{ required: true }]}>
            <AiSimpleEditor
              value={form.getFieldValue('message')}
              placeholder='Compose your email message...'
              onChange={(val) => form.setFieldValue('message', val)}
              className='ai-agent-email-editor'
            />
          </Form.Item>

          {showPreview && (
            <div className='ai-agent-email-preview' role='region' aria-label='Email preview'>
              <div className='preview-header'>
                <span className='preview-title'>Email Preview</span>
                <Button
                  type='link'
                  size='small'
                  onClick={() => setShowPreview(!showPreview)}
                  style={{ marginLeft: 'auto' }}
                  aria-expanded={showPreview}
                  aria-controls='preview-content'
                >
                  {showPreview ? 'Hide' : 'Show'} Preview
                </Button>
              </div>
              <div id='preview-content' className='preview-content'>
                <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
              </div>
            </div>
          )}

          <Divider style={{ margin: '20px 0' }} />

          <Form.Item style={{ marginBottom: 0 }}>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <Button onClick={onClose} disabled={sending}>
                Cancel
              </Button>
              <Button
                type='primary'
                htmlType='submit'
                disabled={sending}
                loading={sending}
                style={{ minWidth: 120 }}
                aria-describedby='send-button-help'
              >
                {sending ? 'Sending...' : 'Send Email'}
              </Button>
            </div>
          </Form.Item>
        </Form>

        {/* Screen reader only help text */}
        <div className='sr-only'>
          <div id='to-field-help'>Enter the recipient&apos;s email address</div>
          <div id='cc-field-help'>Optionally add CC recipients</div>
          <div id='subject-field-help'>Enter a descriptive subject line</div>
          <div id='send-button-help'>Click to send the email</div>
        </div>
      </div>
    </Modal>
  )
}

export { AiAgentEmailModal }
